import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { useState } from 'react';
import { transformFieldWithLang } from './../../utils/functions';
import PendingBookingModal from './PendingBookingModal';
import './PendingPaymentBanner.css';

/**
 * ทางกลับไปหน้าชำระเงิน สำหรับลูกค้าที่กดออกไปแล้ว
 *
 * ก่อนหน้านี้ทางกลับมีอยู่ที่หน้าตะกร้าที่เดียว แต่การจองห้องเดี่ยวไม่ผ่านตะกร้าเลย
 * ลูกค้าที่ปิดหน้าชำระเงินแล้วกลับมาหน้าที่พัก จึงเห็นห้องที่ตัวเองกันไว้ขึ้นว่า "ไม่ว่าง"
 * โดยไม่มีอะไรอธิบาย และต้องรอ 10 นาทีจนระบบปล่อยห้องถึงจะจองใหม่ได้
 *
 * รับสถานะมาจาก usePendingHold เพื่อให้หน้าที่ใช้ร่วมกันได้ (หน้าห้องใช้รายการห้องที่กันไว้ต่อด้วย)
 */
const PendingPaymentBanner = ({ pending, hold, releasing, onResume, onRelease, heldThisRoom = false, rooms = [], stay = null }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'th';
  const navigate = useNavigate();
  const [showDetail, setShowDetail] = useState(false);

  if (!pending?.id) return null;

  const secondsLeft = hold?.seconds_left;
  const clock = typeof secondsLeft === 'number' && secondsLeft > 0
    ? `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')}`
    : null;

  const resume = () => {
    if (typeof onResume === 'function') onResume();
    navigate(`/once/payment/${pending.type}/${pending.id}`, {
      state: { amount: pending.amount, booking_no: pending.booking_no, hotel_id: pending.hotel_id },
    });
  };

  // ชื่อห้องที่กันไว้ -- ถ้ามีหลายห้องจะยาวมาก ตัดเหลือ 3 แล้วบอกว่าเหลืออีกกี่ห้อง
  const roomNames = (rooms || []).map((r) => transformFieldWithLang(r, 'name', lang)).filter(Boolean);
  const shownRooms = roomNames.slice(0, 3).join(' · ');
  const moreRooms = roomNames.length - 3;

  const stayText = stay?.start_date
    ? `${moment(stay.start_date).format('DD/MM/YYYY')} – ${moment(stay.end_date).add(1, 'days').format('DD/MM/YYYY')}`
    : null;

  return (
    <div className="pending-hold" role="status">
      <div className="pending-hold__text">
        <p className="pending-hold__title">
          {t('pending_payment_title')}
          {!!pending.booking_no && <> · {pending.booking_no}</>}
        </p>

        {/* บอกให้ได้ว่าที่ค้างอยู่คือห้องไหน ไม่งั้นลูกค้าต้องกดกลับเข้าไปดูถึงจะรู้ */}
        {(!!roomNames.length || !!stayText) && (
          <p className="pending-hold__rooms">
            {!!roomNames.length && (
              <span className="pending-hold__rooms-name">
                {shownRooms}{moreRooms > 0 && <> {t('and_more_rooms', { count: moreRooms })}</>}
              </span>
            )}
            {!!stayText && <span className="pending-hold__stay">{stayText}</span>}
          </p>
        )}
        <p className="pending-hold__sub">
          {/* อยู่ในหน้ารายละเอียดของห้องที่ตัวเองกันไว้พอดี -- ปฏิทินจะขึ้นว่าเต็ม
            * ต้องบอกตรงนี้ว่าเต็มเพราะอะไร ไม่งั้นข้อความทั่วไปด้านบนไม่ช่วยให้เข้าใจ */}
          {heldThisRoom && <><b>{t('room_held_by_you')}</b> · </>}
          {t('pending_payment_hint')}
          {!!clock && <> <span className="pending-hold__clock">{clock}</span></>}
        </p>
      </div>

      <div className="pending-hold__actions">
        <button type="button" className="pending-hold__btn" onClick={resume} disabled={releasing}>
          {t('pending_payment_resume')}
        </button>
        <button
          type="button"
          className="pending-hold__btn pending-hold__btn--ghost"
          onClick={() => setShowDetail(true)}
          disabled={releasing}
        >
          {t('pending_payment_view')}
        </button>
        <button
          type="button"
          className="pending-hold__btn pending-hold__btn--ghost"
          onClick={onRelease}
          disabled={releasing}
        >
          {releasing ? t('please_wait') : t('pending_payment_cancel_pick_new')}
        </button>
      </div>

      <PendingBookingModal
        show={showDetail}
        onHide={() => setShowDetail(false)}
        pending={pending}
        rooms={rooms}
        stay={stay}
        onResume={resume}
      />
    </div>
  );
};

export default PendingPaymentBanner;
